import "./WatchListHeaderComponent.scss";
import template from "./WatchListHeaderComponent.html";
import {IComponentOptions, IOnDestroy, IOnInit} from "angular";
import {BehaviorSubject, Observable, ReplaySubject} from "rxjs";
import {WatchListStore} from "../../store/WatchListStore";
import {ComponentCallback} from "../../../types/AngularJS";
import {WatchlistSummaries, WatchlistSummary} from "../../models/WatchlistSummary";
import {TextInputDialog} from "../../../services/dialogs/text/TextInputDialog";
import {isNotNull} from "../../../js/util/UtilFunctions";
import {WatchlistType} from "../../models/WatchlistType";
import {WatchlistId} from "../../models/WatchlistId";

export const WatchListHeader = "tfWatchListHeader";

export function watchListHeader(): IComponentOptions {
    return {
        template,
        bindings: {
            selected: "<",
            onSelect: "&",
            onNewItemClick: "&"
        },
        controllerAs: "ctrl",
        controller: WatchListHeaderComponentController
    };
}

export class WatchListHeaderComponentController implements IOnInit, IOnDestroy {

    public readonly selected: WatchlistSummary | null;
    public readonly onSelect: ComponentCallback;
    public readonly onNewItemClick: ComponentCallback;

    public readonly watchlists$ = new ReplaySubject<WatchlistSummaries>(1);
    public readonly loaded$ = new BehaviorSubject(false);
    public readonly hasWatchlists$ = new ReplaySubject<boolean>(1);

    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    /** @ngInject */
    public constructor(private readonly textInputDialog: TextInputDialog,
                       private readonly watchListStore: WatchListStore,
                       private readonly confirmationService: ConfirmationService) {
    }

    public $onInit(): void {
        this.watchListStore
            .watchlists
            .takeUntil(this.destroyed$)
            .map((summaries) => this.sortSummaries(summaries))
            .subscribe((summaries) => this.watchlists$.next(summaries));

        this.watchlists$
            .first()
            .subscribe(() => this.loaded$.next(true));

        this.watchlists$
            .map((wl) => wl.length > 0)
            .subscribe((hasWatchlists) => this.hasWatchlists$.next(hasWatchlists));
    }

    private sortSummaries(summaries: WatchlistSummaries): WatchlistSummaries {
        return [...summaries].sort((a, b) => a.name.localeCompare(b.name));
    }

    public get isModifiable(): boolean {
        return (this.selected != null) && (this.selected.type !== WatchlistType.SYSTEM);
    }

    public async create() {
        const name = await this.textInputDialog.open({title: "Enter Watchlist Name"});
        this.watchForNewWatchlist(name);
        this.watchListStore.create(name);
    }

    private watchForNewWatchlist(name: string) {
        const watchlists$ = this.watchListStore.watchlists;
        const newWatchlist$ = this.watchListStore.newWatchlists;

        Observable.combineLatest(watchlists$, newWatchlist$)
                  .map(([watchlists, id]) => this.findWatchlist(watchlists, id))
                  .filter(isNotNull)
                  .filter((wl) => wl.name === name)
                  .first()
                  .subscribe((watchlist) => this.select(watchlist));
    }

    private findWatchlist(watchlists: WatchlistSummaries, id: WatchlistId): WatchlistSummary | undefined {
        return watchlists.find((wl) => wl.id === id);
    }

    public select(watchlist: WatchlistSummary) {
        this.onSelect({watchlist});
    }

    public delete(watchlist: WatchlistSummary) {
        const confirm = this.confirmationService.confirm("deleteWatchlist");

        Observable.fromPromise(confirm)
                  .catch((err) => Observable.empty<boolean>())
                  .filter((c) => c)
                  .subscribe(() => this.watchListStore.delete(watchlist.id));
    }

    public $onDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
